import { Component, Input, OnInit, Output, EventEmitter, ViewChild, OnDestroy, ContentChild, TemplateRef, ChangeDetectionStrategy, SimpleChanges, ContentChildren, QueryList } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NumberInput } from '@angular/cdk/coercion';
import { BehaviorSubject, isObservable, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { MatSort, SortDirection, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';

import { ComponentConstants } from '@app/shared/constants';
import { GroupByItem } from '@shared/utils/group-by.helper';
import { Span } from '@app/shared/utils/row-span.helper';
import { DataGridExpandChildContent } from './data-grid-expand-child-content';
import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicPipe } from '@app/shared/pipes/dynamic-pipe';
import { CommonPageFilterSetting, CreateCommonPageFilterSetting } from '@app/shared/models/common-data.model';
import { Utilities } from '@app/shared/utils/utilities';
import { LoadingSpinnerComponent } from '@shared/components/loading-spinner/loading-spinner.component';
import { CHECKBOX_COLUMN_NAME, DataGridRowSize, TableColumnDef, TableFooterRow, FieldDataType, DataGridExportFileNames } from '@app/shared/components/smart-data-grid/data-grid/data-grid.constants';

@Component({
  selector: 'app-data-grid',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './data-grid.component.html',
  styleUrls: ['./data-grid.component.css'],
  standalone: true,
  imports: [AngularMaterialModule, CommonModule, TranslateModule, DynamicPipe, LoadingSpinnerComponent],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*', margin: '10px 0 10px 0' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ]
})
export class DataGridComponent<T extends object> implements OnInit, OnDestroy {
  destroy$ = new Subject();
  // csvHelper = inject(CsvHelper);
  @ContentChild('checkBoxHeader') checkBoxHeader: TemplateRef<any>;
  @ContentChild('cellContent') cellContent: TemplateRef<any>;
  @ContentChildren(DataGridExpandChildContent) expandContents!: QueryList<DataGridExpandChildContent>;

  @Input() columnsDef: TableColumnDef[] | Observable<TableColumnDef[]>;
  @Input() dataSource$: Observable<T[]>;
  @Input() dataSourceRowSpans$: Observable<Span[][]>;
  @Input() selectedRows$: Observable<T[]> = new BehaviorSubject<T[]>([]).asObservable();
  @Input() autoSelectSingleResult: boolean = false;
  @Input() enableRowSelect: boolean = false;
  @Input() selectOnRowClick: boolean = true;
  @Input() multiSelect: boolean = true;
  @Input() expandableRows: boolean = false;
  @Input() showMultiExpandableRows: boolean = false;
  @Input() useSingleSelectShowExpandableRows: boolean = false;
  @Input() exportFileName: string = DataGridExportFileNames.DEFAULT;
  @Input() pageSizeOptions: number[] = [5, 10, 20, 50, 100, 200];
  @Input() rowSize: DataGridRowSize = DataGridRowSize.MEDIUM;
  @Input() groupByColumnName: string = "";
  @Input() footerRows$: Observable<TableFooterRow[]>;
  @Input() footerRowCount: number = 0;
  @Input() stickyHeader: boolean = false;
  @Input() enablePagination: boolean = true;

  // For server side paging & filtering
  @Input() isLoading: boolean = false;
  @Input() isServerSidePagingFiltering: boolean = false;
  @Input() totalCountForServerSidePagingFiltering$: Observable<number>;
  @Input() onLoadServerSideData?: (info: CommonPageFilterSetting) => void;
  @Input() defaultPageFilterSetting: CommonPageFilterSetting = CreateCommonPageFilterSetting('');

  @Output() rowSelected = new EventEmitter();
  @Output() pageChanged = new EventEmitter();
  @Output() sortChanged = new EventEmitter();

  dataSource: MatTableDataSource<T> | T[];
  selection: SelectionModel<T>;
  displayedColumns: string[];
  displayedColumnsWithExpand: string[];
  DataGridRowSize = DataGridRowSize;
  _columnsDef: TableColumnDef[];
  expandedElements: any[] = [];
  footerRows: TableFooterRow[];
  footerColumns: string[] = [];
  FieldDataType = FieldDataType;
  dataSourceLength$: Observable<NumberInput>;
  defaultSortDirection = this.defaultPageFilterSetting?.sortDirection as SortDirection;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  constructor() { }

  ngOnInit(): void {
    if (isObservable(this.columnsDef)) {
      this.columnsDef.pipe(takeUntil(this.destroy$)).subscribe(colDef => this.initializeColumnDef(colDef));
    } else {
      this.initializeColumnDef(this.columnsDef);
    }

    if (!this.isServerSidePagingFiltering) {
      this.dataSourceLength$ = this.dataSource$.pipe(filter(Boolean), map(data => data.filter((data: any) => !data.isGroupByHeader).length));
      this.dataSource$.pipe(takeUntil(this.destroy$)).subscribe(data => {
        this.dataSource = new MatTableDataSource(data);
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
      });
    }
    else {
      this.dataSourceLength$ = this.totalCountForServerSidePagingFiltering$.pipe(map(data => data === null || data === undefined ? 0 : data));
      this.dataSource$.pipe(takeUntil(this.destroy$)).subscribe(data => {
        this.dataSource = data;
        this.clearSelection();
      });
    }

    this.selectedRows$.pipe(takeUntil(this.destroy$)).subscribe(selected => {
      this.selection = new SelectionModel<T>(this.multiSelect, selected ?? []);
    });
  }

  loadServerSideData = () => {
    if (!this.onLoadServerSideData) {
      return;
    }

    this.clearSelection();
    var sortBy = this.sort.active ?? '';
    var sortByColumn = this._columnsDef.find(item => item.name === sortBy && !Utilities.isEmptyString(item.sortFieldName));
    sortBy = sortByColumn ? sortByColumn.sortFieldName : sortBy;

    this.onLoadServerSideData({
      sortBy: sortBy,
      sortDirection: this.sort.direction ? this.sort.direction.toString() : '',
      pageSize: this.paginator.pageSize,
      pageIndex: this.paginator.pageIndex
    } as CommonPageFilterSetting);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['enableRowSelect'] && !changes['enableRowSelect'].firstChange) {
      this.enableRowSelect = changes.enableRowSelect.currentValue;
      this.initializeColumnDef(this._columnsDef);
    }
  }

  initializeColumnDef = (columnDef: TableColumnDef[]) => {
    this._columnsDef = columnDef;
    if (this.enableRowSelect) {
      this._columnsDef = [({ text: '', name: CHECKBOX_COLUMN_NAME, sort: false }) as TableColumnDef, ...this._columnsDef];
    } else {
      this._columnsDef = this._columnsDef.filter(i => i.name != CHECKBOX_COLUMN_NAME);
    }

    this.displayedColumns = this._columnsDef.map(c => c.name);
    if (this.expandableRows) {
      this.displayedColumnsWithExpand = [...this.displayedColumns, ComponentConstants.dataGridExpandableColumnName];
    }
    else {
      this.displayedColumnsWithExpand = [...this.displayedColumns];
    }

    this.calculateColumnWidth();
  };

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  setSelectedRow = (data: T) => {
    if (this.enableRowSelect) {
      this.selection?.toggle(data);
      this.rowSelected && this.rowSelected.emit(this.selection?.selected);
    }
  }

  calculateColumnWidth = () => {
    //Calculate column width(they may provided by caller or NOT)
    const totalColumnWidth = this.expandableRows ? (this._columnsDef.length / (this._columnsDef.length + 1) * 100) : 100;
    const unusedWidth = totalColumnWidth - this._columnsDef.map(i => i.width ?? 0).reduce((sum, current) => sum + current, 0),
      unusedColNum = this._columnsDef.filter(i => i.width ? false : true).length;
    this._columnsDef.forEach(col => {
      let _width = 0;
      if (col.name === CHECKBOX_COLUMN_NAME) {
        _width = 3;
      } else {
        _width = col.width ? col.width : unusedWidth / unusedColNum;
      }
      col.widthPercentage = `${_width}%`;
    });
  }

  isAllSelected = () => {
    var data = this.isServerSidePagingFiltering ? this.dataSource as T[] : (this.dataSource as MatTableDataSource<T>).data;
    return this.selection.selected.length === data.filter(i => !(i as any).disableRowSelection).length;
  }

  masterToggle = () => {
    var data = this.isServerSidePagingFiltering ? this.dataSource as T[] : (this.dataSource as MatTableDataSource<T>).data;
    this.isAllSelected() ? this.selection.clear() : data.forEach(row => !(row as any).disableRowSelection && this.selection.select(row));
    this.rowSelected && this.rowSelected.emit(this.selection.selected);
  }

  clearSelection = () => {
    this.selection?.clear();
    this.rowSelected && this.rowSelected.emit([]);
  }

  onPageChanged = (event: PageEvent) => {
    this.loadServerSideData();
    this.pageChanged && this.pageChanged.emit(event);
  }

  // Use for group by UI component check, please do not add logic here
  isGroupByHeader = (index: number, item: GroupByItem) => item.isGroupByHeader;

  isNotGroupByHeader = (index: number, item: GroupByItem) => this.groupByColumnName === "" || !item.isGroupByHeader;

  onShowExpandedRow = (event: any, row: any) => {
    if (this.showMultiExpandableRows) {
      if (this.expandedElements.includes(row)) {
        this.expandedElements.splice(this.expandedElements.indexOf(row), 1);
      }
      else {
        this.expandedElements.push(row);
      }
    }
    else {
      this.expandedElements = this.expandedElements.includes(row) ? [] : [row];
    }

    event.stopPropagation();
  }

  onSortChanged = (event: Sort) => {
    this.expandedElements = [];
    if (this.isServerSidePagingFiltering) {
      this.paginator.pageIndex = 0;
    }

    this.loadServerSideData();
    this.sortChanged && this.sortChanged.emit(event);
  }
}
